/**
 * Authentication Service for VitalAudio Frontend
 * Handles login, logout, and user authentication
 * 
 * NOTE: This service is prepared for Auth0 integration.
 * The hardcoded OTP system has been removed.
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

class AuthService {
  /**
   * Authenticate user with email and password
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Promise<Object>} User data and token
   */
  async login(email, password) {
    try {
      // TODO: Implement Auth0 authentication
      // This will replace the old OTP system
      
      // For now, return an error indicating Auth0 is required
      throw new Error('Auth0 authentication is required. Please configure Auth0 integration.');
      
      // Future Auth0 implementation will go here:
      // 1. Call Auth0 login endpoint
      // 2. Handle MFA if required
      // 3. Return user data and token
      
    } catch (error) {
      console.error('Authentication error:', error);
      throw error;
    }
  }

  /**
   * Logout user and clear stored data
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  /**
   * Get current user from localStorage
   * @returns {Object|null} Current user data or null
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Get current authentication token
   * @returns {string|null} Current token or null
   */
  getToken() {
    return localStorage.getItem('token');
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  /**
   * Get user role for navigation
   * @returns {string} User role
   */
  getUserRole() {
    const user = this.getCurrentUser();
    return user ? user.role : null;
  }

  /**
   * Get redirect URL based on user role
   * @returns {string} Redirect URL
   */
  getRedirectUrl() {
    const role = this.getUserRole();
    
    // Dynamic role-based routing - no hardcoded logic
    const roleRoutes = {
      'SYSTEM_ADMIN': '/sysadmin',
      'tenant_admin': '/tenant-admin',
      'tenant_doctor': '/tenant-doctor',
      'tenant_nurse': '/tenant-nurse',
      'tenant_technician': '/tenant-technician',
      'tenant_patient': '/tenant-patient',
      'tenant_user': '/dashboard'
    };
    
    // Return the appropriate route or default to dashboard
    return roleRoutes[role] || '/dashboard';
  }

  /**
   * Set authentication data (for Auth0 integration)
   * @param {Object} authData - Authentication data from Auth0
   */
  setAuthData(authData) {
    if (authData.access_token) {
      localStorage.setItem('token', authData.access_token);
    }
    if (authData.user) {
      localStorage.setItem('user', JSON.stringify(authData.user));
    }
  }

  /**
   * Clear authentication data
   */
  clearAuthData() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
}

export default new AuthService();
